import { useState, useRef, useMemo } from 'react'
import { QrCode, Copy, Download, X, Upload, Trash2 } from 'lucide-react'
import { QRCodeSVG } from 'qrcode.react'
import { useLanguage } from '../contexts/LanguageContext'
import { Helmet } from 'react-helmet-async'
import FAQAccordion from '../components/FAQAccordion'

type ErrorCorrectionLevel = 'L' | 'M' | 'Q' | 'H'

const QRCodeGenerator = () => {
  const { language } = useLanguage()
  const [text, setText] = useState('')
  const [size, setSize] = useState(256)
  const [errorLevel, setErrorLevel] = useState<ErrorCorrectionLevel>('M')
  const [qrColor, setQrColor] = useState('#000000')
  const [bgColor, setBgColor] = useState('#FFFFFF')
  const [margin, setMargin] = useState(4)
  const [logo, setLogo] = useState<string | null>(null)
  const [logoSize, setLogoSize] = useState(20)
  const [generated, setGenerated] = useState(false)
  const [copied, setCopied] = useState(false)
  const [showPreview, setShowPreview] = useState(false)
  const qrRef = useRef<HTMLDivElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const translations = {
    tr: {
      title: 'Ücretsiz QR Kod Oluşturucu',
      seoTitle: 'Ücretsiz QR Kod Oluşturucu | İstanbul, Türkiye | Thinklyze',
      seoDescription: 'Ücretsiz QR kod oluşturucu aracı ile herhangi bir metin veya link için QR kod oluşturun. İstanbul ve Türkiye genelinde kullanılabilir. Özelleştirilebilir boyut ve hata düzeltme seviyesi. Tamamen ücretsiz.',
      seoKeywords: 'qr kod oluşturucu, qr code generator, ücretsiz qr kod, qr kod generator türkiye, istanbul qr kod, qr kod oluştur, qr code maker',
      description: 'Herhangi bir metin veya link için QR kod oluşturun',
      textLabel: 'Metin veya Link',
      textPlaceholder: 'https://example.com veya herhangi bir metin',
      sizeLabel: 'Boyut',
      sizeRange: '128px - 512px arası boyut seçin',
      colorLabel: 'QR Kod Rengi',
      colorHint: 'QR kodun rengini özelleştirin',
      bgColorLabel: 'Arka Plan Rengi',
      bgColorHint: 'QR kodun arka plan rengini özelleştirin',
      marginLabel: 'Kenar Boşluğu',
      marginHint: 'QR kodun etrafındaki boşluk (0-4)',
      previewLabel: 'Canlı Önizleme',
      logoLabel: 'Logo Ekle',
      logoUpload: 'Logo Yükle',
      logoRemove: 'Logoyu Kaldır',
      logoSizeLabel: 'Logo Boyutu',
      logoSizeHint: 'Logo boyutunu ayarlayın (%10 - %30)',
      logoSizeWarning: 'Logo çok büyük! QR kod okunamayabilir. Hata düzeltme seviyesini yükseltin veya logo boyutunu küçültün.',
      errorLevelLabel: 'Hata Düzeltme Seviyesi',
      errorLevels: {
        L: 'L (Düşük - %7)',
        M: 'M (Orta - %15)',
        Q: 'Q (Yüksek - %25)',
        H: 'H (Çok Yüksek - %30)'
      },
      errorLevelHint: 'Yüksek seviye daha dayanıklı QR kod oluşturur',
      generateButton: 'QR Kod Oluştur',
      clearButton: 'Temizle',
      generatedTitle: 'Oluşturulan QR Kod',
      copyButton: 'Kopyala',
      downloadButton: 'İndir (PNG)',
      downloadSVG: 'İndir (SVG)',
      copied: 'Kopyalandı!',
      invalidInput: 'Lütfen geçerli bir metin veya link girin',
      faqTitle: 'Sık Sorulan Sorular',
      faqs: [
        {
          question: 'QR kod oluşturucu ücretsiz mi?',
          answer: 'Evet, QR kod oluşturucu aracımız tamamen ücretsizdir. Sınırsız sayıda QR kod oluşturabilir ve PNG formatında indirebilirsiniz.'
        },
        {
          question: 'QR kod boyutunu nasıl ayarlayabilirim?',
          answer: 'Boyut slider\'ını kullanarak 128px ile 512px arasında istediğiniz boyutu seçebilirsiniz. 32px adımlarla ayarlama yapabilirsiniz.'
        },
        {
          question: 'Hata düzeltme seviyesi ne işe yarar?',
          answer: 'Hata düzeltme seviyesi, QR kodun hasar gördüğünde bile okunabilir olmasını sağlar. Yüksek seviye (H) daha dayanıklı QR kodlar oluşturur ancak daha fazla alan kaplar.'
        },
        {
          question: 'QR kodumu nasıl kullanabilirim?',
          answer: 'Oluşturduğunuz QR kodu PNG formatında indirebilir, web sitenize, broşürlerinize, kartvizitlerinize veya sosyal medya paylaşımlarınıza ekleyebilirsiniz.'
        },
        {
          question: 'QR kod hangi tür verileri içerebilir?',
          answer: 'QR kodlar URL\'ler, metin, telefon numaraları, e-posta adresleri, WiFi bilgileri ve daha fazlasını içerebilir. Herhangi bir metin veya link için QR kod oluşturabilirsiniz.'
        },
        {
          question: 'QR kodun ortasına logo ekleyebilir miyim?',
          answer: 'Evet! Logo yükleme özelliğimiz ile QR kodunuzun ortasına marka logonuzu ekleyebilirsiniz. Logo boyutunu %10 ile %30 arasında ayarlayabilirsiniz. Logo eklerken yüksek hata düzeltme seviyesi (Q veya H) kullanmanızı öneririz.'
        }
      ]
    },
    en: {
      title: 'Free QR Code Generator',
      seoTitle: 'Free QR Code Generator | Istanbul, Turkey | Thinklyze',
      seoDescription: 'Create QR codes for any text or link with our free QR code generator tool. Available in Istanbul and throughout Turkey. Customizable size and error correction level. Completely free.',
      seoKeywords: 'qr code generator, free qr code, qr code generator turkey, istanbul qr code, create qr code, qr code maker',
      description: 'Create a QR code for any text or link',
      textLabel: 'Text or Link',
      textPlaceholder: 'https://example.com or any text',
      sizeLabel: 'Size',
      sizeRange: 'Select size between 128px - 512px',
      colorLabel: 'QR Code Color',
      colorHint: 'Customize the color of your QR code',
      bgColorLabel: 'Background Color',
      bgColorHint: 'Customize the background color of your QR code',
      marginLabel: 'Margin',
      marginHint: 'Space around QR code (0-4)',
      previewLabel: 'Live Preview',
      logoLabel: 'Add Logo',
      logoUpload: 'Upload Logo',
      logoRemove: 'Remove Logo',
      logoSizeLabel: 'Logo Size',
      logoSizeHint: 'Adjust logo size (10% - 30%)',
      logoSizeWarning: 'Logo too large! QR code may not be readable. Increase error correction level or reduce logo size.',
      errorLevelLabel: 'Error Correction Level',
      errorLevels: {
        L: 'L (Low - 7%)',
        M: 'M (Medium - 15%)',
        Q: 'Q (High - 25%)',
        H: 'H (Very High - 30%)'
      },
      errorLevelHint: 'Higher level creates a more durable QR code',
      generateButton: 'Create QR Code',
      clearButton: 'Clear',
      generatedTitle: 'Generated QR Code',
      copyButton: 'Copy',
      downloadButton: 'Download (PNG)',
      downloadSVG: 'Download (SVG)',
      copied: 'Copied!',
      invalidInput: 'Please enter a valid text or link',
      faqTitle: 'Frequently Asked Questions',
      faqs: [
        {
          question: 'Is the QR code generator free?',
          answer: 'Yes, our QR code generator tool is completely free. You can create unlimited QR codes and download them in PNG format.'
        },
        {
          question: 'How can I adjust the QR code size?',
          answer: 'You can select any size between 128px and 512px using the size slider. You can adjust in 32px increments.'
        },
        {
          question: 'What does error correction level do?',
          answer: 'Error correction level ensures that QR codes can still be read even when damaged. Higher levels (H) create more durable QR codes but take up more space.'
        },
        {
          question: 'How can I use my QR code?',
          answer: 'You can download your QR code in PNG format and add it to your website, brochures, business cards, or social media posts.'
        },
        {
          question: 'What types of data can QR codes contain?',
          answer: 'QR codes can contain URLs, text, phone numbers, email addresses, WiFi information, and more. You can create QR codes for any text or link.'
        },
        {
          question: 'Can I add a logo to the center of my QR code?',
          answer: 'Yes! With our logo upload feature, you can add your brand logo to the center of your QR code. You can adjust the logo size between 10% and 30%. We recommend using a high error correction level (Q or H) when adding a logo.'
        }
      ]
    },
    ru: {
      title: 'Бесплатный генератор QR-кодов',
      seoTitle: 'Бесплатный генератор QR-кодов | Стамбул, Турция | Thinklyze',
      seoDescription: 'Создавайте QR-коды для любого текста или ссылки с помощью нашего бесплатного инструмента. Доступно в Стамбуле и по всей Турции. Настраиваемый размер и уровень коррекции ошибок. Полностью бесплатно.',
      seoKeywords: 'генератор qr кодов, бесплатный qr код, генератор qr кодов турция, стамбул qr код, создать qr код',
      description: 'Создайте QR-код для любого текста или ссылки',
      textLabel: 'Текст или ссылка',
      textPlaceholder: 'https://example.com или любой текст',
      sizeLabel: 'Размер',
      sizeRange: 'Выберите размер от 128px - 512px',
      colorLabel: 'Цвет QR-кода',
      colorHint: 'Настройте цвет вашего QR-кода',
      bgColorLabel: 'Цвет фона',
      bgColorHint: 'Настройте цвет фона вашего QR-кода',
      marginLabel: 'Отступы',
      marginHint: 'Пространство вокруг QR-кода (0-4)',
      previewLabel: 'Предварительный просмотр',
      logoLabel: 'Добавить логотип',
      logoUpload: 'Загрузить логотип',
      logoRemove: 'Удалить логотип',
      logoSizeLabel: 'Размер логотипа',
      logoSizeHint: 'Настройте размер логотипа (10% - 30%)',
      logoSizeWarning: 'Логотип слишком большой! QR-код может быть нечитаемым. Увеличьте уровень коррекции ошибок или уменьшите размер логотипа.',
      errorLevelLabel: 'Уровень коррекции ошибок',
      errorLevels: {
        L: 'L (Низкий - 7%)',
        M: 'M (Средний - 15%)',
        Q: 'Q (Высокий - 25%)',
        H: 'H (Очень высокий - 30%)'
      },
      errorLevelHint: 'Высокий уровень создает более прочный QR-код',
      generateButton: 'Создать QR-код',
      clearButton: 'Очистить',
      generatedTitle: 'Созданный QR-код',
      copyButton: 'Копировать',
      downloadButton: 'Скачать (PNG)',
      downloadSVG: 'Скачать (SVG)',
      copied: 'Скопировано!',
      invalidInput: 'Пожалуйста, введите действительный текст или ссылку',
      faqTitle: 'Часто задаваемые вопросы',
      faqs: [
        {
          question: 'Генератор QR-кодов бесплатный?',
          answer: 'Да, наш инструмент генератора QR-кодов полностью бесплатный. Вы можете создавать неограниченное количество QR-кодов и скачивать их в формате PNG.'
        },
        {
          question: 'Как я могу настроить размер QR-кода?',
          answer: 'Вы можете выбрать любой размер от 128px до 512px с помощью ползунка размера. Вы можете настраивать с шагом 32px.'
        },
        {
          question: 'Что делает уровень коррекции ошибок?',
          answer: 'Уровень коррекции ошибок гарантирует, что QR-коды все еще можно прочитать даже при повреждении. Более высокие уровни (H) создают более прочные QR-коды, но занимают больше места.'
        },
        {
          question: 'Как я могу использовать свой QR-код?',
          answer: 'Вы можете скачать свой QR-код в формате PNG и добавить его на свой веб-сайт, в брошюры, визитные карточки или публикации в социальных сетях.'
        },
        {
          question: 'Какие типы данных могут содержать QR-коды?',
          answer: 'QR-коды могут содержать URL, текст, номера телефонов, адреса электронной почты, информацию о WiFi и многое другое. Вы можете создавать QR-коды для любого текста или ссылки.'
        },
        {
          question: 'Могу ли я добавить логотип в центр моего QR-кода?',
          answer: 'Да! С помощью нашей функции загрузки логотипа вы можете добавить логотип вашего бренда в центр вашего QR-кода. Вы можете настроить размер логотипа от 10% до 30%. Мы рекомендуем использовать высокий уровень коррекции ошибок (Q или H) при добавлении логотипа.'
        }
      ]
    }
  }

  const t = translations[language as keyof typeof translations] || translations.tr

  // Hata düzeltme seviyesine göre maksimum logo boyutu
  const getMaxLogoSize = (level: ErrorCorrectionLevel): number => {
    switch (level) {
      case 'L': return 10  // %7 hata düzeltme -> max %10 logo
      case 'M': return 15  // %15 hata düzeltme -> max %15 logo
      case 'Q': return 20  // %25 hata düzeltme -> max %20 logo
      case 'H': return 25  // %30 hata düzeltme -> max %25 logo
      default: return 15
    }
  }

  const maxLogoSize = getMaxLogoSize(errorLevel)
  const isLogoTooLarge = logo !== null && logoSize > maxLogoSize

  // Hata düzeltme seviyesi değiştiğinde logo boyutunu sınırla
  const handleErrorLevelChange = (newLevel: ErrorCorrectionLevel) => {
    setErrorLevel(newLevel)
    const newMaxSize = getMaxLogoSize(newLevel)
    if (logoSize > newMaxSize) {
      setLogoSize(newMaxSize)
    }
  }

  // SEO Schema Markup
  const breadcrumbSchema = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: language === 'tr' ? 'Ana Sayfa' : language === 'en' ? 'Home' : 'Главная',
        item: 'https://thinklyze.com/'
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: language === 'tr' ? 'Hizmetler' : language === 'en' ? 'Services' : 'Услуги',
        item: 'https://thinklyze.com/services'
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: t.title,
        item: 'https://thinklyze.com/qr-code-generator'
      }
    ]
  }), [language, t.title])

  const howToSchema = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: t.title,
    description: t.description,
    step: [
      {
        '@type': 'HowToStep',
        position: 1,
        name: language === 'tr' ? 'Metin veya link girin' : language === 'en' ? 'Enter text or link' : 'Введите текст или ссылку',
        text: language === 'tr' ? 'Metin veya link alanına QR kod oluşturmak istediğiniz içeriği girin.' : language === 'en' ? 'Enter the content you want to create a QR code for in the text or link field.' : 'Введите содержимое, для которого вы хотите создать QR-код, в поле текста или ссылки.'
      },
      {
        '@type': 'HowToStep',
        position: 2,
        name: language === 'tr' ? 'Boyut ve hata düzeltme seviyesini ayarlayın' : language === 'en' ? 'Adjust size and error correction level' : 'Настройте размер и уровень коррекции ошибок',
        text: language === 'tr' ? 'Slider ile boyutu seçin ve dropdown menüden hata düzeltme seviyesini belirleyin.' : language === 'en' ? 'Select the size using the slider and choose the error correction level from the dropdown menu.' : 'Выберите размер с помощью ползунка и выберите уровень коррекции ошибок из выпадающего меню.'
      },
      {
        '@type': 'HowToStep',
        position: 3,
        name: language === 'tr' ? 'QR kodu oluşturun' : language === 'en' ? 'Generate the QR code' : 'Создайте QR-код',
        text: language === 'tr' ? '\'QR Kod Oluştur\' butonuna tıklayın ve QR kodunuzu görüntüleyin.' : language === 'en' ? 'Click the \'Create QR Code\' button and view your QR code.' : 'Нажмите кнопку \'Создать QR-код\' и просмотрите свой QR-код.'
      }
    ]
  }), [language, t.title, t.description])

  const faqSchema = useMemo(() => {
    if (!t.faqs || t.faqs.length === 0) return null
    return {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: t.faqs.map((faq: any) => ({
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.answer
        }
      }))
    }
  }, [t.faqs])

  const webPageSchema = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: t.seoTitle || t.title,
    description: t.seoDescription || t.description,
    url: 'https://thinklyze.com/qr-code-generator',
    inLanguage: language === 'tr' ? 'tr-TR' : language === 'en' ? 'en-US' : 'ru-RU',
    about: {
      '@type': 'Thing',
      name: 'QR Code Generator',
      description: t.description
    },
    provider: {
      '@type': 'Organization',
      name: 'Thinklyze',
      url: 'https://thinklyze.com',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Bakırköy',
        addressRegion: 'İstanbul',
        addressCountry: 'TR'
      }
    }
  }), [language, t])

  const generateQR = () => {
    if (!text.trim()) {
      alert(t.invalidInput)
      return
    }
    
    // Logo varsa ve çok büyükse uyar
    if (logo && logoSize > maxLogoSize) {
      const confirmMessage = language === 'tr' 
        ? `Logo boyutu (%${logoSize}) mevcut hata düzeltme seviyesi için çok büyük (max: %${maxLogoSize}). QR kod okunamayabilir. Yine de devam etmek istiyor musunuz?`
        : language === 'en'
        ? `Logo size (${logoSize}%) is too large for current error correction level (max: ${maxLogoSize}%). QR code may not be readable. Do you want to continue anyway?`
        : `Размер логотипа (${logoSize}%) слишком велик для текущего уровня коррекции ошибок (макс: ${maxLogoSize}%). QR-код может быть нечитаемым. Вы все равно хотите продолжить?`
      
      if (!window.confirm(confirmMessage)) {
        return
      }
    }
    
    setGenerated(true)
  }

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Sadece resim dosyalarını kabul et
    if (!file.type.startsWith('image/')) {
      alert(language === 'tr' ? 'Lütfen geçerli bir resim dosyası seçin' : language === 'en' ? 'Please select a valid image file' : 'Пожалуйста, выберите действительный файл изображения')
      return
    }

    // Dosya boyutu kontrolü (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      alert(language === 'tr' ? 'Logo dosyası 2MB\'dan küçük olmalıdır' : language === 'en' ? 'Logo file must be smaller than 2MB' : 'Файл логотипа должен быть меньше 2 МБ')
      return
    }

    const reader = new FileReader()
    reader.onload = (event) => {
      const result = event.target?.result
      if (typeof result === 'string') {
        setLogo(result)
        // Logo yüklendiğinde boyutu maksimum değere sınırla
        const currentMaxSize = getMaxLogoSize(errorLevel)
        if (logoSize > currentMaxSize) {
          setLogoSize(currentMaxSize)
        }
      }
    }
    reader.readAsDataURL(file)
  }

  const removeLogo = () => {
    setLogo(null)
    setLogoSize(20)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const clearAll = () => {
    setText('')
    setSize(256)
    setErrorLevel('M')
    setQrColor('#000000')
    setBgColor('#FFFFFF')
    setMargin(4)
    setLogo(null)
    setLogoSize(20)
    setGenerated(false)
    setCopied(false)
    setShowPreview(false)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const copyToClipboard = async () => {
    if (!qrRef.current) return

    try {
      // QR kod SVG'yi canvas'a çevir ve kopyala
      const svg = qrRef.current.querySelector('svg')
      if (!svg) return

      const svgData = new XMLSerializer().serializeToString(svg)
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')
      const img = new Image()

      canvas.width = size
      canvas.height = size

      img.onload = () => {
        if (ctx) {
          ctx.fillStyle = bgColor
          ctx.fillRect(0, 0, canvas.width, canvas.height)
          ctx.drawImage(img, 0, 0)
          
          canvas.toBlob((blob) => {
            if (blob) {
              navigator.clipboard.write([
                new ClipboardItem({ 'image/png': blob })
              ])
              setCopied(true)
              setTimeout(() => setCopied(false), 2000)
            }
          })
        }
      }

      img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)))
    } catch (err) {
      console.error('Kopyalama hatası:', err)
    }
  }

  const downloadQR = () => {
    if (!qrRef.current) return

    const svg = qrRef.current.querySelector('svg')
    if (!svg) return

    const svgData = new XMLSerializer().serializeToString(svg)
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    const img = new Image()

    canvas.width = size
    canvas.height = size

    img.onload = () => {
      if (ctx) {
        ctx.fillStyle = bgColor
        ctx.fillRect(0, 0, canvas.width, canvas.height)
        ctx.drawImage(img, 0, 0)
        
        canvas.toBlob((blob) => {
          if (blob) {
            const url = URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = url
            link.download = `qrcode-${Date.now()}.png`
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            URL.revokeObjectURL(url)
          }
        })
      }
    }

    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)))
  }

  const downloadSVG = () => {
    if (!qrRef.current) return

    const svg = qrRef.current.querySelector('svg')
    if (!svg) return

    const svgData = new XMLSerializer().serializeToString(svg)
    const blob = new Blob([svgData], { type: 'image/svg+xml' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `qrcode-${Date.now()}.svg`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  return (
    <>
      <Helmet>
        <title>{t.seoTitle || t.title} | Thinklyze</title>
        <meta name="description" content={t.seoDescription || t.description} />
        <meta name="keywords" content={t.seoKeywords} />
        <link rel="canonical" href="https://thinklyze.com/qr-code-generator" />
        
        {/* Open Graph Tags */}
        <meta property="og:title" content={t.seoTitle || t.title} />
        <meta property="og:description" content={t.seoDescription || t.description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://thinklyze.com/qr-code-generator" />
        <meta property="og:image" content="https://thinklyze.com/images/logo_siyah.webp" />
        <meta property="og:locale" content={language === 'tr' ? 'tr_TR' : language === 'en' ? 'en_US' : 'ru_RU'} />
        
        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={t.seoTitle || t.title} />
        <meta name="twitter:description" content={t.seoDescription || t.description} />
        <meta name="twitter:image" content="https://thinklyze.com/images/logo_siyah.webp" />
        
        {/* Geo Tags */}
        <meta name="geo.region" content="TR-34" />
        <meta name="geo.placename" content="İstanbul" />
        <meta name="geo.position" content="41.0082;28.9784" />
        <meta name="ICBM" content="41.0082, 28.9784" />
      </Helmet>
      
      {/* Structured Data */}
      <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(howToSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(webPageSchema)}</script>
      {faqSchema && <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>}
      
      <div className="pt-16 bg-[#f9f9f9] relative min-h-screen">
        {/* Background Grid Pattern */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#e5e7eb_1px,transparent_1px),linear-gradient(to_bottom,#e5e7eb_1px,transparent_1px)] bg-[size:80px_80px] [mask-image:radial-gradient(ellipse_60%_40%_at_50%_10%,#000_20%,transparent_70%)] opacity-40 pointer-events-none"></div>
        
        <div className="max-w-4xl mx-auto px-6 py-16 relative z-10">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-3 mb-4">
              <QrCode className="w-8 h-8 text-purple-600" />
              <h1 className="text-4xl font-bold text-gray-900">{t.title}</h1>
            </div>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {t.description}
            </p>
            <p className="text-sm text-gray-500 mt-4 max-w-2xl mx-auto">
              {language === 'tr' 
                ? 'İstanbul, Türkiye\'de dijital pazarlama profesyonelleri için ücretsiz araç' 
                : language === 'en' 
                ? 'Free tool for digital marketing professionals in Istanbul, Turkey' 
                : 'Бесплатный инструмент для специалистов по цифровому маркетингу в Стамбуле, Турция'}
            </p>
          </div>

          {/* Main Card */}
          <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
            {/* Text/Link Input */}
            <div className="mb-6">
              <label htmlFor="text" className="block text-sm font-semibold text-gray-900 mb-2">
                {t.textLabel} *
              </label>
              <input
                type="text"
                id="text"
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder={t.textPlaceholder}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors text-lg"
              />
            </div>

            {/* Settings Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              {/* Size Slider */}
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  {t.sizeLabel}: {size}px
                </label>
                <input
                  type="range"
                  min="128"
                  max="512"
                  step="32"
                  value={size}
                  onChange={(e) => setSize(Number(e.target.value))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-purple-600"
                />
                <p className="text-xs text-gray-500 mt-1">{t.sizeRange}</p>
              </div>

              {/* Error Correction Level */}
              <div>
                <label htmlFor="errorLevel" className="block text-sm font-semibold text-gray-900 mb-2">
                  {t.errorLevelLabel}
                </label>
                <select
                  id="errorLevel"
                  value={errorLevel}
                  onChange={(e) => handleErrorLevelChange(e.target.value as ErrorCorrectionLevel)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors"
                >
                  <option value="L">{t.errorLevels.L}</option>
                  <option value="M">{t.errorLevels.M}</option>
                  <option value="Q">{t.errorLevels.Q}</option>
                  <option value="H">{t.errorLevels.H}</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">{t.errorLevelHint}</p>
              </div>
            </div>

            {/* Color Pickers Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              {/* QR Code Color */}
              <div>
                <label htmlFor="qrColor" className="block text-sm font-semibold text-gray-900 mb-2">
                  {t.colorLabel}
                </label>
                <div className="flex items-center gap-4">
                  <input
                    type="color"
                    id="qrColor"
                    value={qrColor}
                    onChange={(e) => setQrColor(e.target.value)}
                    className="w-16 h-12 border border-gray-300 rounded-lg cursor-pointer"
                  />
                  <input
                    type="text"
                    value={qrColor}
                    onChange={(e) => {
                      const value = e.target.value
                      if (/^#[0-9A-Fa-f]{0,6}$/.test(value)) {
                        setQrColor(value)
                      }
                    }}
                    placeholder="#000000"
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors font-mono"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">{t.colorHint}</p>
              </div>

              {/* Background Color */}
              <div>
                <label htmlFor="bgColor" className="block text-sm font-semibold text-gray-900 mb-2">
                  {t.bgColorLabel}
                </label>
                <div className="flex items-center gap-4">
                  <input
                    type="color"
                    id="bgColor"
                    value={bgColor}
                    onChange={(e) => setBgColor(e.target.value)}
                    className="w-16 h-12 border border-gray-300 rounded-lg cursor-pointer"
                  />
                  <input
                    type="text"
                    value={bgColor}
                    onChange={(e) => {
                      const value = e.target.value
                      if (/^#[0-9A-Fa-f]{0,6}$/.test(value)) {
                        setBgColor(value)
                      }
                    }}
                    placeholder="#FFFFFF"
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors font-mono"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">{t.bgColorHint}</p>
              </div>
            </div>

            {/* Margin Slider */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-900 mb-2">
                {t.marginLabel}: {margin}
              </label>
              <input
                type="range"
                min="0"
                max="4"
                step="1"
                value={margin}
                onChange={(e) => setMargin(Number(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-purple-600"
              />
              <p className="text-xs text-gray-500 mt-1">{t.marginHint}</p>
            </div>

            {/* Live Preview Toggle */}
            {text.trim() && (
              <div className="mb-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showPreview}
                    onChange={(e) => setShowPreview(e.target.checked)}
                    className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                  />
                  <span className="text-sm font-semibold text-gray-900">{t.previewLabel}</span>
                </label>
              </div>
            )}

            {/* Live Preview */}
            {showPreview && text.trim() && (
              <div className="mb-6 p-6 bg-gray-50 rounded-xl border-2 border-purple-200">
                <div className="flex justify-center">
                  <div className="bg-white p-4 rounded-lg border-2 border-gray-200 inline-block">
                    <QRCodeSVG
                      value={text}
                      size={Math.min(size, 200)}
                      level={errorLevel}
                      bgColor={bgColor}
                      fgColor={qrColor}
                      includeMargin={margin > 0}
                      marginSize={margin}
                      imageSettings={logo ? {
                        src: logo,
                        height: (Math.min(size, 200) * logoSize) / 100,
                        width: (Math.min(size, 200) * logoSize) / 100,
                        excavate: true,
                      } : undefined}
                    />
                  </div>
                </div>
                <p className="text-xs text-center text-gray-500 mt-3">
                  {language === 'tr' ? 'Canlı önizleme - QR kod oluşturmadan önce görüntüleyin' : 
                   language === 'en' ? 'Live preview - View before generating QR code' : 
                   'Предварительный просмотр - просмотрите перед созданием QR-кода'}
                </p>
              </div>
            )}

            {/* Logo Upload */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-900 mb-2">
                {t.logoLabel}
              </label>
              {!logo ? (
                <div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    className="hidden"
                    id="logoUpload"
                  />
                  <label
                    htmlFor="logoUpload"
                    className="flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-purple-500 hover:bg-purple-50 transition-colors"
                  >
                    <Upload className="w-5 h-5 text-gray-600" />
                    <span className="text-sm font-medium text-gray-700">{t.logoUpload}</span>
                  </label>
                  <p className="text-xs text-gray-500 mt-1">
                    {language === 'tr' ? 'PNG, JPG veya SVG formatında logo yükleyin (max 2MB)' : 
                     language === 'en' ? 'Upload logo in PNG, JPG or SVG format (max 2MB)' : 
                     'Загрузите логотип в формате PNG, JPG или SVG (макс. 2 МБ)'}
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <img
                      src={logo}
                      alt="Logo preview"
                      className="w-16 h-16 object-contain rounded"
                    />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">
                        {t.logoSizeLabel}: {logoSize}% 
                        {logo && <span className="text-xs text-gray-500 ml-2">(Max: {maxLogoSize}%)</span>}
                      </p>
                      <input
                        type="range"
                        min="10"
                        max={maxLogoSize}
                        step="1"
                        value={Math.min(logoSize, maxLogoSize)}
                        onChange={(e) => {
                          const newSize = Number(e.target.value)
                          if (newSize <= maxLogoSize) {
                            setLogoSize(newSize)
                          }
                        }}
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-purple-600 mt-2"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        {t.logoSizeHint} - {language === 'tr' ? `Mevcut seviye için maksimum: %${maxLogoSize}` : 
                         language === 'en' ? `Maximum for current level: ${maxLogoSize}%` : 
                         `Максимум для текущего уровня: ${maxLogoSize}%`}
                      </p>
                      {isLogoTooLarge && (
                        <p className="text-xs text-red-600 mt-1 font-medium flex items-center gap-1">
                          <span>⚠️</span>
                          {t.logoSizeWarning}
                        </p>
                      )}
                    </div>
                    <button
                      onClick={removeLogo}
                      className="px-3 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors flex items-center gap-2"
                    >
                      <Trash2 className="w-4 h-4" />
                      <span className="text-sm">{t.logoRemove}</span>
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex gap-4">
              <button
                onClick={generateQR}
                className="flex-1 bg-gray-900 text-white font-semibold py-3 px-6 rounded-lg hover:bg-gray-800 transition-colors duration-200"
              >
                {t.generateButton}
              </button>
              <button
                onClick={clearAll}
                className="px-6 py-3 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 transition-colors duration-200 flex items-center gap-2"
              >
                <X className="w-5 h-5" />
                {t.clearButton}
              </button>
            </div>
          </div>

          {/* Generated QR Code */}
          {generated && text.trim() && (
            <div className="bg-white rounded-xl shadow-lg p-8">
              <h2 className="text-xl font-bold text-gray-900 mb-6">{t.generatedTitle}</h2>
              <div className="flex justify-center mb-6">
                <div 
                  ref={qrRef}
                  className="bg-white p-4 rounded-lg border-2 border-gray-200 inline-block"
                >
                  <QRCodeSVG
                    value={text}
                    size={size}
                    level={errorLevel}
                    bgColor={bgColor}
                    fgColor={qrColor}
                    includeMargin={margin > 0}
                    marginSize={margin}
                    imageSettings={logo ? {
                      src: logo,
                      height: (size * logoSize) / 100,
                      width: (size * logoSize) / 100,
                      excavate: true,
                    } : undefined}
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <button
                  onClick={copyToClipboard}
                  className="bg-white border-2 border-gray-300 text-gray-900 font-medium py-3 px-6 rounded-lg hover:bg-gray-50 transition-colors duration-200 flex items-center justify-center gap-2"
                >
                  <Copy className="w-5 h-5" />
                  {copied ? t.copied : t.copyButton}
                </button>
                <button
                  onClick={downloadQR}
                  className="bg-gray-900 text-white font-medium py-3 px-6 rounded-lg hover:bg-gray-800 transition-colors duration-200 flex items-center justify-center gap-2"
                >
                  <Download className="w-5 h-5" />
                  {t.downloadButton}
                </button>
                <button
                  onClick={downloadSVG}
                  className="bg-purple-600 text-white font-medium py-3 px-6 rounded-lg hover:bg-purple-700 transition-colors duration-200 flex items-center justify-center gap-2"
                >
                  <Download className="w-5 h-5" />
                  {t.downloadSVG}
                </button>
              </div>
            </div>
          )}

          {/* FAQ Section */}
          {t.faqs && t.faqs.length > 0 && (
            <div className="bg-white rounded-xl shadow-lg p-8 mt-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">{t.faqTitle}</h2>
              <FAQAccordion 
                faqs={t.faqs.map((faq: any) => ({
                  question: faq.question,
                  answer: faq.answer
                }))}
              />
            </div>
          )}
        </div>
      </div>
    </>
  )
}

export default QRCodeGenerator

